import React from "react";
import { Navigate, Link } from "react-router-dom";
import { Loader2, ArrowLeft, Printer, FileText, EyeOff } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

const FLYERS = [
  {
    slug: "residents",
    title: "General residents",
    description:
      "Bold, urgent flyer for the average Adelaide household. Covers slow PCs, Wi-Fi, scams, $0 callout. A5 portrait.",
    accent: "var(--te-primary)",
  },
  {
    slug: "seniors",
    title: "Seniors",
    description:
      "Calmer tone, larger type. Focuses on patience, no-jargon, no-upsell, scam reassurance. A5 portrait.",
    accent: "var(--te-primary)",
  },
];

export default function FlyersIndex() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--te-bg)" }}>
      <header
        className="border-b border-white/10 sticky top-0 z-30"
        style={{ background: "rgba(5,5,5,0.85)", backdropFilter: "blur(20px)" }}
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link
            to="/admin"
            className="inline-flex items-center gap-2 text-sm font-medium text-zinc-400 hover:text-white transition"
            data-testid="flyers-back-to-admin"
          >
            <ArrowLeft size={14} /> Back to admin dashboard
          </Link>
          <div className="text-[10px] tracking-[0.18em] font-bold uppercase text-zinc-500 hidden sm:inline-flex items-center gap-1.5">
            <EyeOff size={11} /> Internal · not indexed
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="te-overline mb-3">/ Print collateral</div>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tighter leading-tight mb-3">
          Letterbox flyers.
        </h1>
        <p className="text-zinc-400 max-w-xl text-base leading-relaxed mb-10">
          Print-ready, brand-styled A5 flyers for direct distribution. Open one,
          click <strong className="text-white">Print / Save as PDF</strong>, choose
          A5, and you&apos;re done.
        </p>

        <div className="grid sm:grid-cols-2 gap-5">
          {FLYERS.map((f) => (
            <Link
              key={f.slug}
              to={`/admin/flyers/${f.slug}`}
              data-testid={`flyer-card-${f.slug}`}
              className="te-card p-6 group hover:border-white/30 transition"
            >
              <div className="flex items-start justify-between gap-3 mb-4">
                <div
                  className="w-10 h-10 flex items-center justify-center flex-shrink-0"
                  style={{ background: f.accent }}
                >
                  <FileText size={18} color="#fff" />
                </div>
                <div className="text-[10px] tracking-[0.18em] font-bold uppercase text-zinc-500">
                  A5 portrait
                </div>
              </div>
              <h2 className="text-2xl font-bold tracking-tight mb-2 group-hover:text-[var(--te-primary)] transition-colors">
                {f.title}
              </h2>
              <p className="text-sm text-zinc-400 leading-relaxed mb-5">
                {f.description}
              </p>
              <div
                className="text-sm font-semibold inline-flex items-center gap-2"
                style={{ color: "var(--te-primary)" }}
              >
                <Printer size={14} /> Open &amp; print
              </div>
            </Link>
          ))}
        </div>

        <div className="mt-12 te-card p-5 border-white/10">
          <div className="te-overline text-[10px] mb-2">/ Printing tips</div>
          <ul className="text-sm text-zinc-400 leading-relaxed list-disc pl-5 space-y-1">
            <li>Paper size: <strong className="text-white">A5</strong> for one flyer per sheet, <strong className="text-white">A4</strong> with &ldquo;2 pages per sheet&rdquo; for cheap bulk printing.</li>
            <li>Print dialog → <strong className="text-white">Background graphics: ON</strong> (otherwise the black background won&apos;t print).</li>
            <li>Officeworks or local print shops can do 100× A5 colour for around $30–$40.</li>
            <li>These pages are blocked from Google &amp; not in the sitemap — only you (signed in as admin) can see them.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
